import { useEffect, useId, useMemo, useState } from "react";
import { useRouter } from "next/router";
import { useAtomValue, useSetAtom } from "jotai";
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, Typography } from "@mui/material";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import { getOptionsPlatformPlan, OptionsPlatformLaunchOffer, OptionsPlatformPrice, subscribeOptionsPlatform } from "@/controllers/options-platform";
import { agreementsState, appUpgradeLoadingState, planPickerDrawerOpenState, userState } from "@/lib/store";
import useAppSubscriberUpgrade from "src/hooks/useAppSubscriberUpgrade";
import { UPGRADE_LOCATIONS, trackUpgradeClick } from "@/lib/tracking";
import AgreementsForm from "@/components/agreements-form/AgreementsForm";
import { isOrtexApp } from "@/lib/utils";
import styles from "./OptionsPlatformUnlockDialog.module.scss";

type Interval = "month" | "year";

/**
 * Format a money amount for display: always two decimals, OR none when
 * the amount is whole. `9.9 → "9.90"`, `9 → "9"`, `99 → "99"`,
 * `4.95 → "4.95"`. Keeps locale thousand-separators for larger values.
 * `.toLocaleString()` alone produced "9.9" — the bug this fixes.
 */
function formatPrice(amount: number): string {
    const hasFraction = Math.round(amount * 100) % 100 !== 0;
    return amount.toLocaleString(undefined, {
        minimumFractionDigits: hasFraction ? 2 : 0,
        maximumFractionDigits: 2,
    });
}

// Plans without addon-eligibility — these users must upgrade to a paid
// ORTEX subscription before they can buy the Options Platform addon.
//
// At $9.90/mo this addon is priced for mass-market: keeping the same
// gate as Index Rebalance Forecasts for now (open question #2 in
// coption-specs-for-claude/17-paywall-and-access.md §9), but the
// gate is one constant so widening it is a one-line change.
const SUB_REQUIRED_PLANS = ["ORTEX Visitor", "ORTEX Free", "ORTEX Basic"];

const upgradeCtaText: Record<string, string> = {
    "ORTEX Visitor": "Create free account",
    "ORTEX Free": "Upgrade now",
    "ORTEX Basic": "Get Advanced",
};

interface Props {
    open: boolean;
    onClose: () => void;
    /**
     * Hard-paywall mode (AddonGate on /options-platform/*). The page
     * behind this dialog has no usable content for a non-subscriber, so
     * dismissing it only produced a dead blurred screen: no backdrop or
     * Escape close, and "Not now" is replaced by an explicit navigation
     * away. Offers shown over pages the user CAN still use (the legacy
     * options surfaces, the OfferHost promos) must leave this false.
     */
    mandatory?: boolean;
    /** Where "Not now" sends a mandatory-mode user. */
    exitHref?: string;
    exitLabel?: string;
}

interface PriceCardProps {
    label: string;
    currency: string;
    amount: number;
    /** Unit shown right after the amount, e.g. "/mo". Both plans lead
     *  with a monthly figure so the two cards compare like-for-like. */
    unit?: string;
    sublabel: string;
    selected: boolean;
    onClick: () => void;
    badge?: string;
}

const PriceCard = ({ label, currency, amount, unit, sublabel, selected, onClick, badge }: PriceCardProps) => (
    <Box
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick();
            }
        }}
        className={styles.priceCard}
        sx={{
            borderColor: selected ? "primary.main" : "divider",
            backgroundColor: selected ? "action.selected" : "transparent",
            "&:hover": { borderColor: selected ? "primary.main" : "text.secondary" },
            "&:focus-visible": { outlineColor: "primary.main" },
        }}
    >
        {badge && (
            <Box className={styles.badge} sx={{ backgroundColor: "primary.main", color: "primary.contrastText" }}>
                {badge}
            </Box>
        )}
        <Typography variant="caption" className={styles.cardLabel}>
            {label}
        </Typography>
        <Typography variant="h5" component="p" className={styles.cardPrice}>
            {currency}
            {formatPrice(amount)}
            {unit && (
                <Typography component="span" variant="body2" sx={{ fontWeight: 400, opacity: 0.7 }}>
                    {unit}
                </Typography>
            )}
        </Typography>
        <Typography variant="caption" className={styles.cardSublabel}>
            {sublabel}
        </Typography>
    </Box>
);

/**
 * Blocks the Options Platform routes for users who have not purchased
 * the addon. Mirrors IndexRebalUnlockDialog: tries the saved card first,
 * falls back to Stripe Checkout if there isn't one.
 *
 * Spec ref: coption-specs-for-claude/17-paywall-and-access.md.
 */
const OptionsPlatformUnlockDialog = ({ open, onClose, mandatory = false, exitHref = "/", exitLabel = "Not now" }: Props) => {
    // Names the dialog after whichever step title is showing (aria-dialog-name).
    const titleId = useId();
    const user = useAtomValue(userState);
    const appUpgradeLoading = useAtomValue(appUpgradeLoadingState);
    const setPlanPickerOpen = useSetAtom(planPickerDrawerOpenState);
    const { handleAppSubscriberUpgrade } = useAppSubscriberUpgrade();
    const { push, replace } = useRouter();

    // In mandatory mode the only ways out are buying or leaving the
    // page — never a silent dismiss onto unusable content.
    // Explicit over `onClose={undefined}`: MUI reports WHY it wants to
    // close, so mandatory mode can refuse the two implicit routes
    // (clicking the backdrop, pressing Escape) by name and leave the
    // buttons as the only way out. Stated this way it also survives
    // someone later wiring a close affordance without reading this file.
    const dialogClose = (_event: object, reason?: string) => {
        if (mandatory && (reason === "backdropClick" || reason === "escapeKeyDown")) return;
        onClose();
    };
    const handleDecline = () => {
        if (mandatory) {
            // REPLACE, not push: the paywalled page must not stay in
            // history, or the browser Back button lands the user right
            // back on the offer they just declined. Replacing means Back
            // continues past it to wherever they were before.
            replace(exitHref);
            return;
        }
        onClose();
    };

    const needsSubscription = SUB_REQUIRED_PLANS.includes(user?.subscription_plan);

    const [plan, setPlan] = useState<OptionsPlatformPrice | null>(null);
    // Launch offer terms come from the backend so we never hard-code the
    // discount percent in two places. Null = no offer / older backend.
    const [launchOffer, setLaunchOffer] = useState<OptionsPlatformLaunchOffer | null>(null);
    const [loading, setLoading] = useState(false);
    const [purchasing, setPurchasing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);
    const [billingInterval, setBillingInterval] = useState<Interval>("month");
    const [step, setStep] = useState<"choose" | "confirm">("choose");
    // Post-purchase OPRA collection — the paid flow's final step. Shown
    // when the purchase completes and the user hasn't signed OPRA yet;
    // the reload happens after the form closes instead of on a timer.
    const [showOpra, setShowOpra] = useState(false);
    const agreements = useAtomValue(agreementsState);
    const hasOpra = !!agreements?.find((a) => a.code === "OPRA");

    useEffect(() => {
        if (!open || needsSubscription) return;
        setLoading(true);
        setError(null);
        setStep("choose");
        getOptionsPlatformPlan()
            .then((res) => {
                setPlan(res.plan);
                setLaunchOffer(res.launch_offer && res.launch_offer.active ? res.launch_offer : null);
            })
            .catch(() => setError("We couldn't load the price right now. Please try again."))
            .finally(() => setLoading(false));
    }, [open, needsSubscription]);

    const handleSubscribeUpgrade = () => {
        trackUpgradeClick(UPGRADE_LOCATIONS.OPTIONS_PLATFORM_UNLOCK_DIALOG, { plan: user?.subscription_plan });
        onClose();
        if (user?.subscription_plan === "ORTEX Visitor") {
            push("/login?register=true");
        } else if (isOrtexApp()) {
            setPlanPickerOpen(true);
        } else {
            handleAppSubscriberUpgrade();
        }
    };

    const monthlyPrice = useMemo(() => plan?.prices?.find((p) => p.recurring?.interval === "month"), [plan]);
    const yearlyPrice = useMemo(() => plan?.prices?.find((p) => p.recurring?.interval === "year"), [plan]);
    const selectedPrice = billingInterval === "month" ? monthlyPrice : yearlyPrice;
    const hasBothIntervals = Boolean(monthlyPrice && yearlyPrice);

    // Headline savings of yearly vs 12×monthly — this is the structural
    // yearly discount, separate from the launch coupon. Combining the
    // two (e.g. "save 67% in year 1") double-counts and overstates;
    // we surface them separately in the UI.
    const yearlyVsMonthlyPct = monthlyPrice && yearlyPrice ? Math.round(((monthlyPrice.amount * 12 - yearlyPrice.amount) / (monthlyPrice.amount * 12)) * 100) : 0;

    // Single source of truth for the launch-coupon percent — pulled from
    // the backend (`launch_offer.discount_percent`) so the UI can't drift
    // if marketing changes the Stripe coupon. Falls back to 0 / null when
    // the coupon isn't active.
    const launchDiscountPct = launchOffer?.discount_percent ?? 0;
    const launchDurationMonths = launchOffer?.duration_months ?? 0;
    const launchActive = launchDiscountPct > 0 && launchDurationMonths > 0;
    // 100% off the first cycle = "first month free" — rendered as words,
    // not as a crossed-out zero.
    const freeFirstMonth = launchActive && launchDiscountPct === 100;
    const discountedAmount = (full: number) => (launchActive ? full * (1 - launchDiscountPct / 100) : full);

    const handleBuy = async () => {
        if (!selectedPrice?.priceId) return;
        setPurchasing(true);
        setError(null);
        try {
            const res = await subscribeOptionsPlatform(selectedPrice.priceId);
            if (res.purchaseComplete) {
                setSuccess(true);
                if (!hasOpra) {
                    // Final step of the paid flow: collect the OPRA
                    // agreement data before unlocking. Reload happens when
                    // the form closes.
                    setShowOpra(true);
                } else {
                    // Brief pause so the user sees the confirmation, then
                    // reload so the new entitlement is picked up and the
                    // AddonGate unblocks the platform.
                    setTimeout(() => window.location.reload(), 1500);
                }
            }
            // If the response carried a checkoutUrl the controller has
            // already redirected to Stripe.
        } catch {
            setError("Something went wrong starting your subscription. Please try again or contact support@ortex.com.");
        } finally {
            setPurchasing(false);
        }
    };

    if (needsSubscription) {
        return (
            <Dialog open={open} onClose={dialogClose} maxWidth="sm" fullWidth aria-labelledby={titleId}>
                <DialogContent className={styles.dialogContent}>
                    <Box className={styles.iconCircle} sx={{ backgroundColor: "primary.main", color: "primary.contrastText" }}>
                        <ShowChartIcon fontSize="medium" />
                    </Box>
                    <Typography variant="h5" component="h2" id={titleId} className={styles.title}>
                        Subscribe to unlock Options Platform
                    </Typography>
                    <Typography variant="body1" className={styles.body}>
                        The ORTEX Options Platform is a premium add-on for paid ORTEX subscribers. Get a paid plan first, then come back here to add the Options Platform to your subscription.
                    </Typography>
                </DialogContent>
                <DialogActions className={styles.dialogActions}>
                    <Button fullWidth variant="contained" color="primary" size="large" disabled={appUpgradeLoading} onClick={handleSubscribeUpgrade}>
                        {appUpgradeLoading ? <CircularProgress size={20} sx={{ color: "white" }} /> : upgradeCtaText[user?.subscription_plan] || "Upgrade now"}
                    </Button>
                    <Button fullWidth variant="text" onClick={handleDecline} disabled={appUpgradeLoading}>
                        {exitLabel}
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }

    return (
        <Dialog open={open} onClose={dialogClose} maxWidth="sm" fullWidth aria-labelledby={titleId}>
            <DialogContent className={styles.dialogContent}>
                <Box className={styles.iconCircle} sx={{ backgroundColor: "primary.main", color: "primary.contrastText" }}>
                    <ShowChartIcon fontSize="medium" />
                </Box>
                {step === "choose" ? (
                    <>
                        {launchActive && (
                            <Box
                                sx={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                    px: 1,
                                    py: 0.25,
                                    mb: 1.5,
                                    borderRadius: 1,
                                    backgroundColor: "#F59E0B",
                                    color: "#000",
                                    fontSize: 11,
                                    fontWeight: 800,
                                    letterSpacing: 0.3,
                                    textTransform: "uppercase",
                                }}
                            >
                                {freeFirstMonth ? "First month free" : `Launch offer · ${launchDiscountPct}% off first ${launchDurationMonths} months`}
                            </Box>
                        )}
                        <Typography variant="h5" component="h2" id={titleId} className={styles.title}>
                            Unlock the ORTEX Options Platform
                        </Typography>
                        <Typography variant="body1" className={styles.body}>
                            A professional options chain with Greeks and probability of profit on every strike, an interactive P&amp;L diagram you can scrub by price, date or IV, and a multi-leg strategy builder. The ORTEX short-interest sidebar sits
                            alongside it, with real-time flow and squeeze alerts, GEX levels, an IV-rank screener and earnings IV-crush history. One workspace for all of it.
                        </Typography>
                        <Typography variant="caption" sx={{ display: "block", textAlign: "left", mb: 2 }}>
                            <strong>Why is it a separate add-on?</strong> OPRA&apos;s licensing now requires the full options dataset to be sold on its own. It does a good deal more than the classic options pages ever did.
                        </Typography>
                        {loading ? (
                            <CircularProgress size={28} sx={{ marginY: 2 }} />
                        ) : plan && hasBothIntervals && monthlyPrice && yearlyPrice ? (
                            <Box className={styles.priceRow}>
                                {/* Both cards LEAD WITH A MONTHLY figure so they
                                 * compare like-for-like: the annual card shows
                                 * its per-month equivalent (yearly ÷ 12) and
                                 * names the once-a-year charge in the sublabel.
                                 * PROMO: monthly = half price for 6 months;
                                 * yearly = half off the first year. The backend
                                 * applies the coupon on first-time subscribe;
                                 * this just previews what the user pays now.
                                 */}
                                <PriceCard
                                    label="Monthly"
                                    currency={plan.currency}
                                    amount={discountedAmount(monthlyPrice.amount)}
                                    unit="/mo"
                                    sublabel={launchActive ? `Then ${plan.currency}${formatPrice(monthlyPrice.amount)}/mo after ${launchDurationMonths} months` : `Billed monthly`}
                                    selected={billingInterval === "month"}
                                    onClick={() => setBillingInterval("month")}
                                    badge={launchActive ? `${launchDiscountPct}% OFF · ${launchDurationMonths} MO` : undefined}
                                />
                                <PriceCard
                                    label="Annual"
                                    currency={plan.currency}
                                    amount={discountedAmount(yearlyPrice.amount) / 12}
                                    unit="/mo"
                                    sublabel={
                                        launchActive
                                            ? `${plan.currency}${formatPrice(discountedAmount(yearlyPrice.amount))} for year 1, then ${plan.currency}${formatPrice(yearlyPrice.amount)}/yr`
                                            : `Billed annually at ${plan.currency}${formatPrice(yearlyPrice.amount)}/yr · save ${yearlyVsMonthlyPct}%`
                                    }
                                    selected={billingInterval === "year"}
                                    onClick={() => setBillingInterval("year")}
                                    badge={launchActive ? `${launchDiscountPct}% OFF · YEAR 1` : yearlyVsMonthlyPct > 0 ? `SAVE ${yearlyVsMonthlyPct}%` : undefined}
                                />
                            </Box>
                        ) : plan && selectedPrice ? (
                            <>
                                <Typography variant="h6" component="p" className={styles.singlePrice}>
                                    {plan.currency}
                                    {formatPrice(selectedPrice.amount)}
                                    <Typography component="span" variant="body2" className={styles.priceInterval}>
                                        /{selectedPrice.recurring?.interval === "year" ? "yr" : "mo"}
                                    </Typography>
                                </Typography>
                                {freeFirstMonth && (
                                    <Typography variant="body2" sx={{ color: "#F59E0B", fontWeight: 700, mb: 1 }}>
                                        Your first month is free: billing starts month 2. Cancel anytime.
                                    </Typography>
                                )}
                            </>
                        ) : null}
                        <Typography variant="body2" className={styles.disclaimer}>
                            We&apos;ll charge the card you have on file. If there isn&apos;t one, we&apos;ll send you to Stripe&apos;s secure checkout to add it, and your existing ORTEX subscription stays exactly as it is.
                        </Typography>
                        {error && (
                            <Typography variant="body2" color="error" className={styles.statusMessage}>
                                {error}
                            </Typography>
                        )}
                    </>
                ) : (
                    <>
                        <Typography variant="h5" component="h2" id={titleId} className={styles.title}>
                            Confirm your subscription
                        </Typography>
                        <Typography variant="body1" className={styles.body}>
                            I&apos;m happy for my card on file to be charged
                        </Typography>
                        {selectedPrice && (
                            <>
                                <Typography variant="h4" className={styles.confirmAmount}>
                                    {freeFirstMonth ? "Free today" : `${plan?.currency}${discountedAmount(selectedPrice.amount).toFixed(2)} today`}
                                </Typography>
                                {launchActive && (
                                    <Typography variant="caption" sx={{ display: "block", color: "#F59E0B", fontWeight: 700, mb: 0.5 }}>
                                        {freeFirstMonth ? "First month free: applied automatically" : `Launch offer applied: ${launchDiscountPct}% off`}
                                    </Typography>
                                )}
                                <Typography variant="body2" className={styles.confirmCadence}>
                                    {selectedPrice.recurring?.interval === "year"
                                        ? launchActive
                                            ? `Then ${plan?.currency}${selectedPrice.amount.toFixed(2)} yearly from year 2.`
                                            : `Then ${plan?.currency}${selectedPrice.amount.toFixed(2)} yearly.`
                                        : freeFirstMonth
                                          ? `Then ${plan?.currency}${selectedPrice.amount.toFixed(2)} a month from month 2.`
                                          : launchActive
                                            ? `Then ${plan?.currency}${selectedPrice.amount.toFixed(2)} monthly after ${launchDurationMonths} months.`
                                            : `Then ${plan?.currency}${selectedPrice.amount.toFixed(2)} monthly.`}{" "}
                                    Cancel any time from Settings.
                                </Typography>
                            </>
                        )}
                        <Typography variant="body2" className={styles.disclaimer}>
                            If we don&apos;t have a card on file you&apos;ll be sent to Stripe&apos;s secure checkout to add one. Cancel any time from Settings.
                        </Typography>
                        {error && (
                            <Typography variant="body2" color="error" className={styles.statusMessage}>
                                {error}
                            </Typography>
                        )}
                        {success && (
                            <Typography variant="body2" color="primary" className={styles.statusMessage}>
                                You&apos;re all set. Reloading to unlock the platform.
                            </Typography>
                        )}
                    </>
                )}
            </DialogContent>
            <DialogActions className={styles.dialogActions}>
                {step === "choose" ? (
                    <>
                        <Button
                            fullWidth
                            variant="contained"
                            color="primary"
                            size="large"
                            disabled={!plan || !selectedPrice}
                            onClick={() => {
                                setError(null);
                                setStep("confirm");
                            }}
                        >
                            Unlock Options Platform
                        </Button>
                        <Button fullWidth variant="text" onClick={handleDecline}>
                            {exitLabel}
                        </Button>
                    </>
                ) : (
                    <>
                        <Button fullWidth variant="contained" color="primary" size="large" disabled={!selectedPrice || purchasing || success} onClick={handleBuy}>
                            {purchasing ? "Starting subscription…" : success ? "Unlocked" : freeFirstMonth ? "Yes: start my free month" : "Yes, charge my card"}
                        </Button>
                        <Button fullWidth variant="text" onClick={() => setStep("choose")} disabled={purchasing || success}>
                            Back
                        </Button>
                    </>
                )}
            </DialogActions>
            {/* Post-purchase OPRA data collection (saved to the DB via the
                modern agreements API). Closing it — signed or not —
                reloads; if unsigned the AddonGate re-prompts. */}
            {showOpra && (
                <AgreementsForm
                    isOpen={showOpra}
                    onClose={() => {
                        setShowOpra(false);
                        window.location.reload();
                    }}
                />
            )}
        </Dialog>
    );
};

export default OptionsPlatformUnlockDialog;
